import Column from "theme/layouts/Column";
import Div from "theme/layouts/Div";
import PlusIcon from "theme/components/icons/PlusIcon";
import PropTypes from "prop-types";
import TextStyles from "theme/components/text/TextStyles";
import colors from "theme/config/colors";
import styled from "theme/libs/styled";
import transitions from "theme/config/transitions";
import { useState } from "react";
import Button from "core-web/components/base/Button";

const shouldForwardProp = (prop) => ["isOpen", "borderColor", "activeBorderColor"].indexOf(prop) === -1;

const AccordionItemWrapper = styled(Column, { shouldForwardProp })`
    border: 1px solid ${({ borderColor }) => borderColor};
    border-radius: 12px;
    padding-bottom: ${({ isOpen }) => (isOpen ? "24px" : "0")};
    transition: all ${({ theme }) => theme.transitions.primary};

    .faq-label {
        transition: opacity ${({ theme }) => theme.transitions.primary};
        opacity: 0.7;
    }

    &[data-is-active="true"],
    :hover {
        border-color: ${({ activeBorderColor }) => activeBorderColor};

        .faq-label {
            opacity: 1;
        }
    }
`;

const StyledLabelWrapper = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    cursor: pointer;
`;

const AccordionItem = ({
    id,
    children,
    label,
    open,
    typography,
    labelColor,
    borderColor,
    activeBorderColor,
    ...rest
}) => {
    const [isOpen, setOpen] = useState(open);

    const handleClick = () => {
        setOpen(!isOpen);
    };

    return (
        <AccordionItemWrapper
            borderColor={borderColor}
            data-is-active={isOpen}
            activeBorderColor={activeBorderColor}
            isOpen={isOpen}
            {...rest}
        >
            <StyledLabelWrapper
                id={`${id}-accordion-toggle`}
                aria-expanded={isOpen}
                aria-controls={`${id}-accordion-content`}
                onClick={() => handleClick()}
                p="24px 16px"
            >
                <TextStyles
                    textStyles={typography}
                    className="faq-label"
                    data-is-active={isOpen}
                    display="block"
                    userSelect="none"
                    color={labelColor}
                >
                    {label}
                </TextStyles>
                <PlusIcon isActive={isOpen} width="16px" />
            </StyledLabelWrapper>

            <Div
                id={`${id}-accordion-content`}
                aria-labelledby={`${id}-accordion-toggle`}
                role="region"
                display="grid"
                gridTemplateRows={isOpen ? "1fr" : "0fr"}
                transition={`grid-template-rows ${transitions.primary}`}
            >
                <Div overflow="hidden">
                    <Div px="16px">{children}</Div>
                </Div>
            </Div>
        </AccordionItemWrapper>
    );
};

AccordionItem.propTypes = {
    activeBorderColor: PropTypes.string,
    borderColor: PropTypes.string,
    labelColor: PropTypes.string,
    children: PropTypes.node.isRequired,
    id: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    open: PropTypes.bool,
    typography: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
};

AccordionItem.defaultProps = {
    activeBorderColor: colors.black,
    borderColor: colors.neutral_100,
    labelColor: colors.black,
    open: false,
    typography: "Myriad Pro/20-100",
};

export default AccordionItem;
